import React from 'react';
import styled from 'styled-components';
import { EventRequest } from '../hooks/useEventRequests';
import { supabase } from '../services/supabase';

type Props = {
  request: EventRequest;
  onApprove: (notes?: string) => void;
  onPublish: (notes?: string) => void;
  onDecline: (notes?: string) => void;
  onNeedInfo: (notes?: string) => void;
  onImageChange: (imageUrl: string) => Promise<void> | void;
  onSaveEdits: (payload: {
    title: string;
    description: string | null;
    ministry: string | null;
    event_type: string | null;
    location: string | null;
    start_date: string | null;
    start_time: string | null;
    share_date: string | null;
  }) => Promise<void> | void;
  isUpdating?: boolean;
  isSavingEdits?: boolean;
};

export const EventApprovalCard: React.FC<Props> = ({
  request,
  onApprove,
  onPublish,
  onDecline,
  onNeedInfo,
  onImageChange,
  onSaveEdits,
  isUpdating,
  isSavingEdits,
}) => {
  const [note, setNote] = React.useState(request.notes ?? '');
  const [notesOpen, setNotesOpen] = React.useState(false);
  const [imageUrl, setImageUrl] = React.useState(request.image_url ?? '');
  const [, setIsEditing] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for effect sync and future onSaveEdits
  const [editForm, setEditForm] = React.useState({
    title: request.title ?? '',
    description: request.description ?? '',
    ministry: request.ministry ?? '',
    event_type: normalizeEventTypeOption(request.event_type),
    location: request.location ?? '',
    start_date: request.start_date ?? '',
    start_time: request.start_time ?? '',
    share_date: request.share_date ?? '',
  });
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const { key: statusKey, label: statusLabel } = React.useMemo(
    () => deriveStatus(request),
    [request]
  );

  const relativeTimeLabel = React.useMemo(
    () => (request.submitted_at ? formatRelativeTime(request.submitted_at) : null),
    [request.submitted_at]
  );
  const shortDateLabel = React.useMemo(
    () => (request.start_date ? formatShortDate(request.start_date) : null),
    [request.start_date]
  );

  React.useEffect(() => {
    setNote(request.notes ?? '');
    setImageUrl(request.image_url ?? '');
    setEditForm({
      title: request.title ?? '',
      description: request.description ?? '',
      ministry: request.ministry ?? '',
      event_type: normalizeEventTypeOption(request.event_type),
      location: request.location ?? '',
      start_date: request.start_date ?? '',
      start_time: request.start_time ?? '',
      share_date: request.share_date ?? '',
    });
    setIsEditing(false);
  }, [
    request.id,
    request.notes,
    request.image_url,
    request.title,
    request.description,
    request.ministry,
    request.event_type,
    request.location,
    request.start_date,
    request.start_time,
    request.share_date,
  ]);

  const uploadToStorage = async (file: File) => {
    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `event-requests/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('post-images').upload(fileName, file, {
      upsert: false,
    });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleFileChange = async (file?: File) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      window.alert('Please upload an image smaller than 5MB.');
      return;
    }

    setUploading(true);
    try {
      const uploadedUrl = await uploadToStorage(file);
      setImageUrl(uploadedUrl);
      await onImageChange(uploadedUrl);
    } catch (err) {
      console.error(err);
      window.alert('Image upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Card>
      <CardImageCol>
        {imageUrl ? (
          <CardImage src={imageUrl} alt={request.title} />
        ) : (
          <CardImagePlaceholder>No image</CardImagePlaceholder>
        )}
        <StatusBadge data-status={statusKey}>{statusLabel}</StatusBadge>
        <CardImageUpload>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange(e.target.files?.[0])}
            disabled={uploading || isUpdating}
          />
          <span>{uploading ? 'Uploading…' : 'Change'}</span>
        </CardImageUpload>
      </CardImageCol>

      <CardBody>
        <CategoryTags>
          {request.event_type ? <CategoryTag>{request.event_type}</CategoryTag> : null}
          {request.ministry ? <CategoryTag $accent>{request.ministry}</CategoryTag> : null}
        </CategoryTags>
        {relativeTimeLabel ? <RelativeTime>{relativeTimeLabel}</RelativeTime> : null}
        <CardTitle>{request.title || 'Untitled event'}</CardTitle>
        <CardMeta>
          {shortDateLabel ? (
            <CardMetaItem>
              <span aria-hidden>📅</span> {shortDateLabel}
            </CardMetaItem>
          ) : null}
          {request.start_time ? (
            <CardMetaItem>
              <span aria-hidden>🕐</span> {request.start_time}
            </CardMetaItem>
          ) : null}
          {request.location ? (
            <CardMetaItem>
              <span aria-hidden>📍</span> {request.location}
            </CardMetaItem>
          ) : null}
          {request.contact_name ? (
            <CardMetaItem>
              <span aria-hidden>👤</span> {request.contact_name}
            </CardMetaItem>
          ) : null}
        </CardMeta>
        {request.description ? (
          <CardDescription>{request.description}</CardDescription>
        ) : null}
        <CardSecondaryActions>
          <ActionButton onClick={() => onPublish(note)} data-variant="primary" disabled={isUpdating}>
            Open Event
          </ActionButton>
          <ActionButton onClick={() => onDecline(note)} data-variant="danger" disabled={isUpdating}>
            Decline
          </ActionButton>
          <ActionButton onClick={() => onNeedInfo(note)} disabled={isUpdating}>
            Need more info
          </ActionButton>
          <IconButton type="button" onClick={() => setNotesOpen((o) => !o)} aria-label="Notes" title="Notes">
            📝
          </IconButton>
        </CardSecondaryActions>
        {notesOpen ? (
          <Field>
            <Label>Notes</Label>
            <NoteInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add context (optional)" rows={2} disabled={isUpdating} />
          </Field>
        ) : null}
      </CardBody>
    </Card>
  );
};

function deriveStatus(request: EventRequest) {
  const normalizedStatus = request.status?.toLowerCase().replace(/\s+/g, '_') ?? null;
  const resolved =
    normalizedStatus && normalizedStatus !== 'pending'
      ? request.status
      : request.sheet_status ?? request.status ?? 'pending';
  const key = resolved.toLowerCase().replace(/\s+/g, '_');
  const label = resolved || 'Pending';
  return { key, label };
}

function formatDate(value?: string | null) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { timeZone: 'UTC' });
}

function normalizeEventTypeOption(value?: string | null): 'Event' | 'General Announcement' {
  const normalized = (value ?? '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (normalized === 'general announcement' || normalized === 'general annoucement') {
    return 'General Announcement';
  }
  return 'Event';
}

function formatRelativeTime(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatDate(value) ?? '';
}

function formatShortDate(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const month = d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  const day = d.getDate();
  return `${month} ${day}`;
}

const Card = styled.article`
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 0;
  background: var(--surface);
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 0;
  min-height: 200px;
  overflow: hidden;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }
`;

const CardImageCol = styled.div`
  position: relative;
  min-width: 180px;
  min-height: 160px;
  background: var(--surfaceVariant);
  @media (max-width: 720px) {
    min-height: 200px;
  }
`;
const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  min-height: 160px;
  @media (max-width: 720px) {
    min-height: 200px;
  }
`;
const CardImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  min-height: 160px;
  display: grid;
  place-items: center;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
  @media (max-width: 720px) {
    min-height: 200px;
  }
`;
const StatusBadge = styled.span`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: var(--surface);
  color: var(--onSurface);
  border: 1px solid var(--outline);
  &[data-status='approved'] {
    background: var(--successContainer);
    border-color: var(--success);
    color: var(--onSuccessContainer);
  }
  &[data-status='declined'] {
    background: var(--errorContainer);
    border-color: var(--error);
    color: var(--onErrorContainer);
  }
  &[data-status='needs_info'] {
    background: var(--warningContainer);
    border-color: var(--warning);
    color: var(--onWarningContainer);
  }
  &[data-status='pending'] {
    background: var(--warningContainer);
    border-color: var(--warning);
    color: var(--onWarningContainer);
  }
  &[data-status='on_hold'] {
    background: var(--surfaceVariant);
    color: var(--onSurfaceVariant);
  }
  &[data-status='completed'] {
    background: var(--successContainer);
    border-color: var(--success);
    color: var(--onSuccessContainer);
  }
`;
const CardImageUpload = styled.label`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 0.75rem;
  cursor: pointer;
  input {
    display: none;
  }
`;

const CardBody = styled.div`
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
`;
const CategoryTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;
const CategoryTag = styled.span<{ $accent?: boolean }>`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  background: var(--surfaceVariant);
  color: var(--onSurfaceVariant);
  ${({ $accent }) => $accent && `background: var(--primary); color: var(--onPrimary);`}
`;
const RelativeTime = styled.span`
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;
const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--onSurface);
  line-height: 1.3;
`;
const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;
const CardMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;
const CardDescription = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const CardSecondaryActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-top: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--outline);
`;

const Label = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 0.85rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const NoteInput = styled.textarea`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.6rem;
  font-size: 0.95rem;
  resize: vertical;
  min-height: 80px;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  &:disabled {
    background: var(--elevation2);
    cursor: not-allowed;
  }
`;

const ActionButton = styled.button`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.5rem 0.9rem;
  background: var(--surface);
  color: var(--onSurface);
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
  font-weight: 600;

  &[data-variant='primary'] {
    background: var(--primary);
    border-color: var(--primaryDark);
    color: var(--onPrimary);
  }

  &[data-variant='secondary'] {
    background: var(--surfaceVariant);
    border-color: var(--outline);
    color: var(--primary);
  }
  &[data-variant='danger'] {
    background: var(--error);
    border-color: var(--error);
    color: var(--onError);
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const IconButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  transition: background 0.15s ease, transform 0.05s ease;

  &:hover:not(:disabled) {
    background: var(--surfaceVariant);
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default EventApprovalCard;

